// src/pages/Metrics/components/GroupSelector.js
import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Popover, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDesktopLayout } from "../../../components/layout/AppLayout";
import BottomSheet from "../../../components/motion/BottomSheet";
import { ALL, isChain, isNestedOption, parseSelection, selectionLabel, selectionTitle } from "../../../utils/restaurant";

const ALL_GROUP = ALL;
const ACCENT = "#ff7043";
const MENU_SURFACE = "#202024";
const MENU_BORDER = "1px solid rgba(255,255,255,0.08)";
const MENU_MIN_WIDTH = 200;
// На телефоне заголовок списка показываем, только если пунктов достаточно много
const MOBILE_TITLE_MIN_HALLS = 3;

/**
 * Список залов: role="listbox", навигация стрелками, Enter/пробел выбирает.
 * Один и тот же для выпадающего меню (десктоп) и листа снизу (телефон).
 */
// disabled — пункты, которые нельзя выбрать на этом экране (с подписью disabledHint)
function GroupOptions({ groups, selectedGroup, onSelect, itemHeight, labelId, listTitle, disabled = [], disabledHint }) {
  const optionRefs = useRef([]);
  // «Все» — первым, после него разделитель
  const hasAll = groups.includes(ALL_GROUP);
  // Недоступные на этом экране — в конце списка (порядок внутри частей прежний; группы — с их рестораном)
  const rest = groups.filter((group) => group !== ALL_GROUP);
  const available = rest.filter((group) => !disabled.includes(group));
  const unavailableOptions = rest.filter((group) => disabled.includes(group));
  const options = [...(hasAll ? [ALL_GROUP] : []), ...available, ...unavailableOptions];
  const firstUnavailable = available.length > 0 && unavailableOptions.length > 0 ? options.indexOf(unavailableOptions[0]) : -1;

  // При открытии фокус на выбранном пункте
  useEffect(() => {
    const index = Math.max(0, options.indexOf(selectedGroup));
    optionRefs.current[index]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (current, delta) => {
    const next = Math.min(options.length - 1, Math.max(0, current + delta));
    optionRefs.current[next]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(index, 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(index, -1);
    } else if (event.key === "Home") {
      event.preventDefault();
      move(index, -options.length);
    } else if (event.key === "End") {
      event.preventDefault();
      move(index, options.length);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled.includes(options[index])) onSelect(options[index]);
    }
  };

  if (options.length === 0) {
    return (
      <Typography sx={{ px: "10px", py: 1.5, fontSize: "14px", color: "#71717a" }}>Нет данных</Typography>
    );
  }

  return (
    <Box
      role="listbox"
      aria-label={labelId ? undefined : listTitle}
      aria-labelledby={labelId}
      sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
    >
      {options.map((group, index) => {
        const selected = group === selectedGroup;
        const unavailable = disabled.includes(group);
        return (
          <React.Fragment key={group}>
            {index === firstUnavailable && (
              <Box role="separator" sx={{ height: "1px", my: "4px", backgroundColor: "rgba(255,255,255,0.06)" }} />
            )}
            <Box
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              role="option"
              aria-selected={selected}
              aria-disabled={unavailable || undefined}
              tabIndex={-1}
              title={selectionTitle(group, options)}
              onClick={unavailable ? undefined : () => onSelect(group)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              sx={{
                minHeight: itemHeight,
                boxSizing: "border-box",
                px: "10px",
                // Группа под своим рестораном — с отступом
                pl: isNestedOption(group, options) ? "26px" : "10px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRadius: "8px",
                cursor: unavailable ? "default" : "pointer",
                outline: "none",
                fontSize: "14px",
                color: selected ? "#fff" : unavailable ? "#71717a" : "#e4e4e7",
                fontWeight: selected ? 600 : 400,
                backgroundColor: selected ? "rgba(255,112,67,0.10)" : "transparent",
                "&:hover, &:focus-visible": {
                  backgroundColor: selected ? "rgba(255,112,67,0.14)" : unavailable ? "transparent" : "rgba(255,255,255,0.06)",
                },
              }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectionLabel(group)}
              </Box>
              {unavailable && disabledHint && (
                <Box component="span" sx={{ flexShrink: 0, fontSize: "12px", color: "#71717a" }}>
                  {disabledHint}
                </Box>
              )}
              {selected && <CheckIcon aria-hidden="true" sx={{ fontSize: 16, color: ACCENT, flexShrink: 0 }} />}
            </Box>
            {hasAll && index === 0 && options.length > 1 && (
              <Box role="separator" sx={{ height: "1px", my: "4px", backgroundColor: "rgba(255,255,255,0.06)" }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// Лист снизу для телефона: общий BottomSheet (свайп вниз, Esc и клик по затемнению закрывают)
function GroupSheet({ open, onClose, children }) {
  return (
    <BottomSheet open={open} onClose={onClose} ariaLabel="Выбор ресторана" paperSx={{ maxHeight: "70vh", backgroundColor: MENU_SURFACE, borderTop: MENU_BORDER }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", px: "6px", pb: 1.5 }}>
        {children}
      </Box>
    </BottomSheet>
  );
}

/**
 * Выбор ресторана (торговое предприятие iiko, поле Department — см. utils/restaurant).
 * Кнопка показывает название ресторана, а при выбранном зале — «ресторан · зал».
 */
// disabledGroups — пункты, недоступные на этом экране (например, на «Кухне» — рестораны не из iikoCloud)
const GroupSelector = ({ groups = [], selectedGroup, onSelectGroup, activeServerName, disabledGroups = [], disabledHint, sx = {} }) => {
  const desktop = useDesktopLayout();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);

  // Кнопка: «Все» — имя сервера; ресторан сети — его название; группа — «ресторан · группа»,
  // а у одного ресторана — «сервер · группа», как раньше
  const selection = parseSelection(selectedGroup);
  const nested = isNestedOption(selectedGroup, groups);
  const label = !selection
    ? activeServerName || ALL_GROUP
    : selection.group && !nested
    ? [activeServerName, selection.group].filter(Boolean).join(" · ")
    : selectionTitle(selectedGroup, groups);
  const hallsCount = groups.filter((group) => group !== ALL_GROUP).length; // пункты без «Все»
  // В списке рестораны (сеть) или только группы одного ресторана
  const listTitle = isChain(groups) ? "Рестораны" : "Группы";

  const handleOpen = () => {
    setMenuWidth(triggerRef.current?.offsetWidth || 0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Фокус возвращаем на кнопку (Popover делает это сам, лист — здесь)
    triggerRef.current?.focus();
  };

  const handleSelect = (group) => {
    if (onSelectGroup) onSelectGroup(group);
    handleClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        // На телефоне — слева в строке с колокольчиком (FiltersTopRow), занимает остаток ширины
        width: { xs: "100%", md: "auto" },
        maxWidth: { xs: "100%", md: "none" },
        flexShrink: { xs: 1, md: 0 },
        px: 0,
        mb: 0,
        justifyContent: "flex-start",
        minWidth: 0,
        ...sx,
      }}
    >
      <ButtonBase
        ref={triggerRef}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
        sx={{
          // Зона нажатия ≥ 44px, визуально кнопка 36px
          minHeight: 44,
          maxWidth: { xs: "100%", md: 320 },
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          borderRadius: "10px",
          "&:hover .group-trigger": { backgroundColor: "rgba(255,255,255,0.1)" },
          "&.Mui-focusVisible .group-trigger": { outline: `2px solid ${ACCENT}`, outlineOffset: "2px" },
        }}
      >
        <Box
          className="group-trigger"
          sx={{
            height: 36,
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            px: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            borderRadius: "10px",
            backgroundColor: "rgba(255,255,255,0.06)",
            transition: "background-color 0.2s ease",
            userSelect: "none",
          }}
        >
          <Typography
            noWrap
            sx={{ fontSize: "0.9rem", color: "#fff", fontWeight: 500, minWidth: 0 }}
          >
            {label}
          </Typography>
          {selection && (
            <Box aria-hidden="true" sx={{ flexShrink: 0, width: 6, height: 6, borderRadius: "50%", backgroundColor: ACCENT }} />
          )}
          <KeyboardArrowDownIcon
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              fontSize: 20,
              color: "#fff",
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </Box>
      </ButtonBase>

      {desktop ? (
        <Popover
          open={open}
          anchorEl={triggerRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: "6px",
                p: "6px",
                minWidth: Math.max(MENU_MIN_WIDTH, menuWidth),
                maxHeight: "60vh",
                backgroundColor: MENU_SURFACE,
                backgroundImage: "none",
                border: MENU_BORDER,
                borderRadius: "12px",
                boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
              },
            },
          }}
        >
          <Typography id="group-menu-title" sx={{ px: "10px", py: "6px", fontSize: "12px", fontWeight: 600, color: "#8e8e93" }}>
            {listTitle}
          </Typography>
          <GroupOptions
            groups={groups}
            selectedGroup={selectedGroup}
            onSelect={handleSelect}
            itemHeight={40}
            labelId="group-menu-title"
            listTitle={listTitle}
            disabled={disabledGroups}
            disabledHint={disabledHint}
          />
        </Popover>
      ) : (
        <GroupSheet open={open} onClose={handleClose}>
          {hallsCount >= MOBILE_TITLE_MIN_HALLS && (
            <Typography id="group-sheet-title" sx={{ px: "10px", py: "6px", fontSize: "12px", fontWeight: 600, color: "#8e8e93" }}>
              {listTitle}
            </Typography>
          )}
          <GroupOptions
            groups={groups}
            selectedGroup={selectedGroup}
            onSelect={handleSelect}
            itemHeight={48}
            labelId={hallsCount >= MOBILE_TITLE_MIN_HALLS ? "group-sheet-title" : undefined}
            listTitle={listTitle}
            disabled={disabledGroups}
            disabledHint={disabledHint}
          />
        </GroupSheet>
      )}
    </Box>
  );
};

export default GroupSelector;
